import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import { Currency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import {
  CompensationMethod,
  DraftReturn,
  ReturnTotalCalculations,
} from "@redotech/redo-model/draft-return/draft-return";
import {
  compensationMethodHasRepair,
  getCombinedReturnItem,
} from "@redotech/redo-model/draft-return/util";
import {
  getResource,
  Resource,
} from "@redotech/redo-model/localization/resource";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { RepairFeeCollectionStrategy } from "@redotech/redo-model/warranties";
import { capitalizeWords } from "@redotech/string/util";
import { arraysEqualIgnoreOrder } from "@redotech/util/equal";
import { useContext, useEffect, useState } from "react";
import { ReturnContext } from "../../contexts/return";
import { RpcClientContext } from "../../contexts/rpc";
import { CustomerPortalAppSettings } from "../../settings";
import { Subtotal } from "../../types";

function quantizedSubtotal(
  title: string,
  total: number,
  quantity: number,
): Subtotal {
  return {
    title: title + (quantity > 1 ? ` x${quantity}` : ""),
    total: total * quantity,
  };
}

export enum SummaryCardType {
  NORMAL = "normal",
  REPAIR = "repair",
}

/**
 * Resolves a "Hide warranty item costs" toggle. The setting only applies to
 * warranty flows and defaults to off, so item and draft-order amounts are only
 * hidden when the return is a warranty AND the merchant enabled the toggle.
 * Page-agnostic: pass the relevant page's stored flag (review or confirmation).
 */
export function resolveHideItemCosts(
  returnType: ReturnTypeEnum | undefined,
  hideWarrantyItemCosts: boolean | null | undefined,
): boolean {
  if (returnType !== ReturnTypeEnum.WARRANTY) {
    return false;
  }
  return hideWarrantyItemCosts ?? false;
}

function determineSummaryCardType(
  compensationMethod: CompensationMethod | undefined,
): SummaryCardType {
  if (compensationMethod && compensationMethodHasRepair(compensationMethod)) {
    return SummaryCardType.REPAIR;
  }
  return SummaryCardType.NORMAL;
}

export const useSummaryCardReview = ({
  settings,
  customization,
  draftReturn,
  calculations,
  compensationMethod,
  copyResource,
  alert,
}: {
  settings: CustomerPortalAppSettings | undefined;
  customization?: PortalCustomization | null;
  draftReturn: DraftReturn;
  calculations: ReturnTotalCalculations;
  compensationMethod: CompensationMethod | undefined;
  copyResource: Resource;
  alert: (message: string) => void;
}) => {
  const { t } = useTranslation();
  const getLocalizedAdjustmentName = (
    adjustmentName: string | undefined,
    defaultKey: "bonus" | "fee",
  ): string => {
    if (!adjustmentName) {
      return defaultKey === "bonus" ? t("bonus") : t("fee");
    }
    const normalizedName = adjustmentName.trim().toLowerCase();
    if (normalizedName === "bonus") {
      return t("bonus");
    }
    if (normalizedName === "fee") {
      return t("fee");
    }
    return adjustmentName;
  };
  const defaultResource = getResource({});
  const isMerchant = draftReturn.merchantCreated ?? false;
  const returnContext = useContext(ReturnContext);
  const client = useRequiredContext(RpcClientContext);
  const [discountCodes, setDiscountCodes] = useState<string[]>([]);
  const [newDiscountCode, setNewDiscountCode] = useState<string>("");
  const [applyAutomaticDiscounts, setApplyAutomaticDiscounts] = useState(false);

  useEffect(() => {
    setDiscountCodes(draftReturn.newOrderData?.discountCodes ?? []);
    setApplyAutomaticDiscounts(
      !draftReturn.newOrderData?.blockAutomaticDiscounts,
    );
  }, [draftReturn]);

  const [addDiscountCodesLoad, doAddDiscountCodes] = useTriggerLoad(
    async (signal) => {
      await addCustomerDiscountCodes(signal);
    },
  );

  const addCustomerDiscountCodes = useHandler(async (signal: AbortSignal) => {
    if (!returnContext.draftReturn) {
      throw new Error("No draft return found");
    }
    const { draftReturn: updatedDraftReturn } =
      await client.addCustomerDiscountCodes(
        {
          draftReturnId: returnContext.draftReturn._id,
          discountCodes,
          applyAutomaticDiscounts,
        },
        { signal },
      );

    returnContext.setDraftReturn(updatedDraftReturn);
    if (
      !arraysEqualIgnoreOrder(
        discountCodes,
        updatedDraftReturn.newOrderData?.discountCodes ?? [],
      )
    ) {
      const missingDiscountCodes = discountCodes.filter(
        (code) =>
          !(updatedDraftReturn.newOrderData?.discountCodes ?? [])
            .map((existingCode) => existingCode.toLowerCase())
            .includes(code.toLowerCase()),
      );
      alert(
        `Unable to apply discount codes: ${missingDiscountCodes.join(", ")}`,
      );
    }
  });

  const {
    totalTaxes,
    totalRefund,
    totalStoreCredit,
    shippingFee,
    totalShippingFee,
    pickupFee,
    returnValue,
    newOrderValue,
    totalNewOrderTaxes,
    totalPaymentOwed,
    labelDeducted,
    totalRepairFee,
    totalLoyaltyPointsDeducted,
    totalLoyaltyPointsRefunded,
    preDiscountShopNowBonus,
    combinedUnusedUseItOrLoseItBonuses,
  } = calculations;

  const presentmentCurrency =
    draftReturn.presentmentCurrency ?? draftReturn.currency ?? Currency.USD;

  // Get exchange shipping fee from selected option
  const selectedExchangeShippingOption =
    draftReturn.selectedExchangeShippingOption;
  const exchangeShippingFee = selectedExchangeShippingOption?.priceSet
    ? parseFloat(
        selectedExchangeShippingOption.priceSet.presentmentMoney.amount,
      )
    : selectedExchangeShippingOption?.price;
  const exchangeShippingName = selectedExchangeShippingOption?.name || "";

  const { totalAdjustmentValue: adjustmentValue } = calculations;

  const taxSubtotal: Subtotal[] = totalTaxes
    ? [{ title: t("Taxes"), total: totalTaxes }]
    : [];

  const pointsDeductedFromRefundSubtotal: Subtotal | undefined =
    calculations.totalReturnValueDeductedDueToLoyaltyPoints
      ? {
          title: t("Loyalty Points Deducted"),
          total: -calculations.totalReturnValueDeductedDueToLoyaltyPoints,
        }
      : undefined;

  const productSubtotals: Subtotal[] = draftReturn.pendingReturnItems.flatMap(
    (pendingReturnItem) => {
      const combinedItem = getCombinedReturnItem(
        draftReturn,
        pendingReturnItem.id,
      );

      return combinedItem.returnableItem.products.reduce<Subtotal[]>(
        (acc, product) => {
          if (
            combinedItem.returnableItem.isExpanded &&
            !product?.isUnbundled &&
            !product?.isExpandedPrimaryProduct
          ) {
            return acc;
          }
          const quantity = pendingReturnItem.quantity ?? 1;
          acc.push(
            quantizedSubtotal(product.title, +product.returnValue, quantity),
          );
          return acc;
        },
        [],
      );
    },
  );

  const newOrderDiscount = calculations.newOrderDiscount ?? 0;
  const newOrderDiscountSubtotals: Subtotal[] =
    newOrderDiscount > 0
      ? [{ title: t("Discount"), total: -newOrderDiscount }]
      : [];

  const newOrderTaxes: Subtotal[] = totalNewOrderTaxes
    ? [{ title: t("Taxes"), total: totalNewOrderTaxes }]
    : [];

  const newVariantExchangeItemSubtotals: Subtotal[] = Object.entries(
    compensationMethod?.itemValues || {},
  )
    .flatMap(([id, item]) => {
      if (!item.isVariantExchange) {
        return null;
      }
      const combinedItem = getCombinedReturnItem(draftReturn, id);
      const quantity = combinedItem.pendingReturnItem?.quantity ?? 1;
      return combinedItem.returnableItem.products.map((product) =>
        quantizedSubtotal(product.title, +product.price, quantity),
      );
    })
    .filter((item) => item != null);

  const newOrderItemsSubtotals: Subtotal[] = (
    draftReturn?.newOrderData?.items || []
  ).map((item) => {
    return quantizedSubtotal(
      item.title,
      parseFloat(item.priceSet?.presentmentMoney?.amount ?? item.price),
      item.quantity,
    );
  });

  const repairFeeCollectionStrategy =
    draftReturn.repairFeeCollectionStrategy ??
    RepairFeeCollectionStrategy.DEFERRED;

  const isPending = addDiscountCodesLoad.pending;

  const showDiscountCodes =
    customization?.pages?.review?.behavior?.showDiscountCodes ??
    !settings?.theme.hideDiscountCodes;

  // Warranty-only review setting: hide item and draft order amounts while
  // still surfacing return shipping costs. Only applies to warranty flows.
  const hideItemCosts = resolveHideItemCosts(
    draftReturn.returnType,
    customization?.pages?.review?.behavior?.hideWarrantyItemCosts,
  );

  return {
    adjustmentValue,
    adjustmentValueString: capitalizeWords(
      adjustmentValue > 0
        ? getLocalizedAdjustmentName(
            customization?.pages?.review?.text?.adjustmentPositiveName ||
              settings?.returnAdjustment?.positiveName,
            "bonus",
          )
        : getLocalizedAdjustmentName(
            customization?.pages?.review?.text?.adjustmentNegativeName ||
              settings?.returnAdjustment?.negativeName,
            "fee",
          ),
    ),
    currency: presentmentCurrency,
    feesHeader:
      copyResource.FEES_HEADER === defaultResource.FEES_HEADER
        ? t("Return value")
        : copyResource.FEES_HEADER,
    feesOrderHeader:
      copyResource.FEES_ORDER_HEADER === defaultResource.FEES_ORDER_HEADER
        ? t("New order")
        : copyResource.FEES_ORDER_HEADER,
    repairFeeCollectionStrategy,
    labelDeducted,
    newOrderDiscountSubtotals,
    newOrderItemsSubtotals,
    newOrderTaxes,
    newOrderValue,
    newVariantExchangeItemSubtotals,
    totalPaymentOwed,
    pickupFee,
    productSubtotals,
    returnValue,
    shippingFee,
    totalShippingFee,
    exchangeShippingFee,
    exchangeShippingName,
    taxSubtotals: taxSubtotal,
    pointsDeductedFromRefundSubtotal,
    totalRefund,
    totalStoreCredit,
    totalLoyaltyPointsDeducted,
    totalLoyaltyPointsRefunded,
    preDiscountShopNowBonus,
    combinedUnusedUseItOrLoseItBonuses,
    summaryCardType: determineSummaryCardType(compensationMethod),
    hideItemCosts,
    totalRepairFee,
    customerDiscountInputs:
      !isMerchant &&
      !!draftReturn.newOrderData &&
      settings?.exchanges.allowCustomerDiscountCodes &&
      showDiscountCodes
        ? {
            texts: {
              discountInputLabel: t("Discount codes"),
              discountInputDescription: t(
                "These discounts will be applied to this order",
              ),
            },
            inputs: {
              enabled: !isPending,
              discountCodes,
              onChangeDiscountCodes: (discountCodes: string[]) => {
                setDiscountCodes(discountCodes);
                doAddDiscountCodes();
              },
              newDiscountCode,
              onChangeNewDiscountCode: setNewDiscountCode,
            },
          }
        : undefined,
  };
};
